import { useState } from 'react';
import { Box, Typography, Button, Paper, Grid, TextField, MenuItem, Stack, Autocomplete } from '@mui/material';
import { Plus } from 'lucide-react';
import { useTeachers } from '@/hooks/useTeachers';
import { useTeacherAssignments } from '@/hooks/useTeacherAssignments';
import { Loader } from '@/components/feedback/Loader';
import { TeacherAssignmentCard } from './components/TeacherAssignmentCard';
import { useSchool } from '@/hooks/useSchool';
import teacherService from '@/services/teacherService';
import toast from 'react-hot-toast';

export function TeacherAssignmentsPage() {
  const { schoolId } = useSchool();
  const { teachers, loading: teachersLoading } = useTeachers();
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  
  const { assignments, loading: assignmentsLoading, refetch } = useTeacherAssignments(selectedTeacherId);

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await teacherService.removeAssignment(schoolId, assignmentId);
      toast.success('Assignment removed successfully');
      refetch();
    } catch (err) {
      toast.error('Failed to remove assignment');
    }
  };

  if (teachersLoading) return <Loader />;

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Teacher Assignments
        </Typography>
        <Typography color="text.secondary">
          Assign classes, sections, and subjects to teachers
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" mb={4}>Select Teacher</Typography>
            <Autocomplete
              options={teachers}
              getOptionLabel={(t) => `${t.firstName} ${t.lastName} (${t.employeeId})`}
              value={teachers.find((t) => t.id === selectedTeacherId) || null}
              onChange={(e, newValue) => setSelectedTeacherId(newValue ? newValue.id : '')}
              renderInput={(params) => <TextField {...params} label="Teacher" />}
              fullWidth
            />
          </Paper>

          {selectedTeacherId && (
            <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" mb={4}>Assign New Class</Typography>
              {/* Form placeholder for assigning new class */}
              <Stack spacing={3}>
                <TextField select fullWidth label="Class" defaultValue="">
                  <MenuItem value="class-1">Grade 1</MenuItem>
                  <MenuItem value="class-2">Grade 2</MenuItem>
                </TextField>
                <TextField select fullWidth label="Section" defaultValue="">
                  <MenuItem value="sec-1">Section A</MenuItem>
                  <MenuItem value="sec-2">Section B</MenuItem>
                </TextField>
                <TextField select fullWidth label="Subject" defaultValue="">
                  <MenuItem value="Mathematics">Mathematics</MenuItem>
                  <MenuItem value="Science">Science</MenuItem>
                  <MenuItem value="English">English</MenuItem>
                </TextField>
                <Button variant="contained" startIcon={<Plus size={18} />}>
                  Assign Class
                </Button>
              </Stack>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, borderRadius: 2, minHeight: 400 }}>
            <Typography variant="h6" mb={3}>Current Assignments</Typography>
            
            {!selectedTeacherId ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">Select a teacher to view their assignments</Typography>
              </Box>
            ) : assignmentsLoading ? (
              <Loader />
            ) : assignments?.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">No classes assigned to this teacher yet.</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {assignments.map(assignment => (
                  <TeacherAssignmentCard 
                    key={assignment.id} 
                    assignment={assignment} 
                    onDelete={handleRemoveAssignment}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
